import React from 'react';
import PropTypes from 'prop-types';
import PictionJS from 'piction-js';
import { Map as immMap } from 'immutable';
import _ from 'lodash';
import { format } from 'date-fns';
import { injectIntl } from 'react-intl';
import { formatBytes, formatDuration } from '../../utils/dataUtils';
import MetadataViewItem from '../MetadataView/MetadataViewItem';
import appConfig from '../../appConfig';
import { DATE_FORMAT } from '../../constants/session';

function UMODataFields({
  umo,
  session,
  dispatch,
  intl,
}) {
  // origData is the general dataset that returns with each umo
  // values like height/width/size are usually populated only for photos
  let origData = umo.data.o && umo.data.o.find(item => item.$ && item.$.o === '1');
  origData = origData ? origData.$ : false;
  const date = umo.data.dc[0] && format(umo.data.dc[0], appConfig.dateFormat || DATE_FORMAT);
  // return only the specific type of the image
  const type = origData ? _.split(origData.mt, '/')[1].toUpperCase() : umo.type;
  let size;
  let height;
  let width;
  let duration;
  let durationField;
  switch (umo.type) {
    case 'PHOTO':
      if (origData) {
        size = origData.sz ? formatBytes(origData.sz, 2) : '';
        height = origData.h;
        width = origData.w;
      }
      break;
    case 'VIDEO':
      height = umo.metadataList.getMetadata('VIDEO.HEIGHT') ? umo.metadataList.getMetadata('VIDEO.HEIGHT').value : '';
      width = umo.metadataList.getMetadata('VIDEO.WIDTH') ? umo.metadataList.getMetadata('VIDEO.WIDTH').value : '';
      size = umo.metadataList.getMetadata('VIDEO.FILESIZE') ? formatBytes(umo.metadataList.getMetadata('VIDEO.FILESIZE'), 2) : '';
      durationField = umo.metadataList.getMetadata('VIDEO.DURATION');
      duration = durationField && !isNaN(durationField.value) ? formatDuration(durationField.value) : '';
      break;
    case 'AUDIO':
      size = umo.metadataList.getMetadata('AUDIO.FILESIZE') ? formatBytes(umo.metadataList.getMetadata('AUDIO.FILESIZE'), 2) : '';
      durationField = umo.metadataList.getMetadata('AUDIO.DURATION');
      duration = durationField && !isNaN(durationField.value) ? formatDuration(durationField.value) : '';
      break;
    default:
      size = origData && origData.sz ? formatBytes(origData.sz, 2) : '';
      break;
  }

  let listViewItems = [];
  if (appConfig.listViewMetatags) {
    listViewItems = Array.isArray(appConfig.listViewMetatags) ?
      appConfig.listViewMetatags : [appConfig.listViewMetatags];
  }

  const metaFields = appConfig.listViewIncludeTombstoneData
    ? umo.tombstoneMetadataList.all.map((tombstoneItem, i) => {
      if (!listViewItems.includes(tombstoneItem.fullName)) {
        return (
          <MetadataViewItem
            key={`${tombstoneItem.id}${i > 0 && `_${i}`}`}
            metadata={tombstoneItem}
            showValueOnly={false}
            parseList={session.get('parsedMetatags')}
            keywordDelim={session.get('keywordDelimiter')}
            dispatch={dispatch}
            customClass="list-item-tombstone"
          />
        );
      }
    }) : [];

  if (appConfig.listViewMetatags) {
    const groupedMetadata = new Map();

    listViewItems.forEach((itemName) => {
      const matchingMetadata = umo.metadataList.all.filter(metadata => metadata.fullName === itemName);
      matchingMetadata.forEach((metadata) => {
        if (groupedMetadata.has(metadata.id)) {
          groupedMetadata.get(metadata.id)
            .push(metadata);
        } else {
          groupedMetadata.set(metadata.id, [metadata]);
        }
      });
    });

    for (const [key, value] of groupedMetadata) {
      if (_.isArray(value)) {
        value.forEach(
          (metadata, i) => metaFields.push(
            <MetadataViewItem
              key={`${metadata.id}${i > 0 && `_${i}`}`}
              metadata={metadata}
              showValueOnly={i > 0}
              parseList={session.get('parsedMetatags')}
              keywordDelim={session.get('keywordDelimiter')}
              dispatch={dispatch}
              customClass="list-item-tombstone"
            />
          )
        );
      }
    }
  }
  return (
    <div className="row">
      {!appConfig.listViewRemoveMediaData ?
        <div className="col-sm-3">
          <li key={`fileType_${umo.id}`} className="meta-table__item list-item-tombstone">
            <div className="list-item-tombstone-name list-item-tombstone__base">
              {intl.formatMessage({ id: 'umoListItem.type' })}</div>
            <div className="meta-table__item-value">{type}</div>
          </li>
          <li key={`fileSize_${umo.id}`} className="meta-table__item list-item-tombstone">
            <div className="list-item-tombstone-name list-item-tombstone__base">
              {intl.formatMessage({ id: 'umoListItem.size' })}</div>
            <div className="meta-table__item-value">{size}</div>
          </li>
          <li key={`fileHeight_${umo.id}`} className="meta-table__item list-item-tombstone">
            <div className="list-item-tombstone-name list-item-tombstone__base">
              {intl.formatMessage({ id: 'umoListItem.height' })}</div>
            <div className="meta-table__item-value">{height}</div>
          </li>
          <li key={`fileWidth_${umo.id}`} className="meta-table__item list-item-tombstone">
            <div className="list-item-tombstone-name list-item-tombstone__base">
              {intl.formatMessage({ id: 'umoListItem.width' })}</div>
            <div className="meta-table__item-value">{width}</div>
          </li>
          <li key={`fileDuration_${umo.id}`} className="meta-table__item list-item-tombstone">
            <div className="list-item-tombstone-name list-item-tombstone__base">
              {intl.formatMessage({ id: 'umoListItem.duration' })}</div>
            <div className="meta-table__item-value">{duration}</div>
          </li>
          <li key={`fileDate_${umo.id}`} className="meta-table__item list-item-tombstone">
            <div className="list-item-tombstone-name list-item-tombstone__base">
              {intl.formatMessage({ id: 'umoListItem.dateCreated' })}</div>
            <div className="meta-table__item-value">{date}</div>
          </li>
        </div> : null}
      <div className="col-sm-8 list-item-tombstone-view">{metaFields}</div>
    </div>
  );
}
UMODataFields.propTypes = {
  umo: PropTypes.instanceOf(PictionJS.UMO).isRequired,
  session: PropTypes.instanceOf(immMap),
  dispatch: PropTypes.func,
};
export default injectIntl(UMODataFields);
